import {
  IconButton,
  Stack,
  Typography,
  Paper,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  ToggleButtonGroup,
  ToggleButton,
  Box,
  Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import ltcpRoutes from "../constants/ltcpRoutes";
import ReactECharts from "echarts-for-react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import DownloadIcon from "@mui/icons-material/Download";
import { useContext, useEffect, useRef, useState } from "react";
import { LTCPContext } from "../LTCPContext";
import {
  getDemandData,
  getDemandFilters,
} from "../api/ltcpBackend";
import {
  BRANCH_CODE_TO_NAME as BRANCH_STATE_MAPPING,
  CLUSTER_CODE_TO_NAME as CLUSTER_STATE_MAPPING,
} from "LTCP Module/constants/mapping.js";
import MultiSearchFilter from "LTCP Module/components/MultiSearchFilter";
import {
  branchFilterValueConvert,
  clusterFilterValueConvert,
  monthFilterValueConvert,
} from "utils/filterValueConverter";
import { useMsal } from "@azure/msal-react";
import { roundOffNumber } from "utils/roundOff";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import { OverviewButton } from "./DashboardPage";
import DemandNestedRow from "LTCP Module/pages/DemandNestedRow";
import {HIERARCHY_HEADER_LABELS } from "LTCP Module/constants/demandHierarchy.js";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const COLLAPSED_HIERARCHY_COLUMN_WIDTH = 150;
const EXPANDED_HIERARCHY_COLUMN_WIDTH = 560;
const DATA_COLUMN_WIDTH = 110;

const monthNames = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const createLabel = (text, position) => {
  return {
    type: "text",
    left: position,
    top: 0,
    style: {
      text,
      fill: "#333",
      fontSize: "12px",
      fontWeight: "bold",
    },
  };
};


// This is the demand summary page where we are collecting the demand
// data on this page load based on the default filters and on any filter
// change it will refect the data from the server and will refresh the
// table and graph. Based on the time granularity it will show either
// MoM or QoQ or YoY data.
function DemandPage() {
  const navigate = useNavigate();
  const tableRef = useRef();
  const { accounts } = useMsal();
  const activeAccount = accounts[0];
  const userEmail = activeAccount.username;

  const {
    demandFile,
    demandFilters,
    setLoading,
    runtime,
    runtype,
    loading,
    commonSelectedFilters,
    setCommonSelectedFilters,
    demandSelectedFilters,
    setDemandSelectedFilters,
  } = useContext(LTCPContext);

  const [currentFilters, setCurrentFilters] = useState(demandFilters);
  const [expandAll, setExpandAll] = useState(false);
  const [isHierarchyColumnExpanded, setIsHierarchyColumnExpanded] = useState(false);
  
  const hierarchyColumnWidth = isHierarchyColumnExpanded
  ? EXPANDED_HIERARCHY_COLUMN_WIDTH
  : COLLAPSED_HIERARCHY_COLUMN_WIDTH;

  const {
    tableLevel,
    clusterState,
    depotState,
    format1State,
  } = demandSelectedFilters;

  const {
    bgState,
    subcategoryState,
    branchState,
    brandState,
    formatState,
    basepackState,
    factoryState,
    yearState,
    quarterState,
    monthState,
    load_ym,
  } = commonSelectedFilters;
  const commonAppliedFilterNames = [
    "bg",
    "subcategory",
    "branch",
    "brand",
    "format",
    "basepack",
    "factory",
    "year",
    "quarter",
    "month",
  ];
  const appliedFilters = [
    ...commonSelectedFilters.appliedFilters,
    ...demandSelectedFilters.appliedFilters,
  ];
  const timeGranularity = commonSelectedFilters.timeGranularity || "MoM";

  const [visibleHierarchyLevel, setVisibleHierarchyLevel] = useState(0);

  const setCommonSelectedFilter = (key, value) =>
    setCommonSelectedFilters((prev) => ({ ...prev, [key]: value }));
  const setDemandSelectedFilter = (key, value) =>
    setDemandSelectedFilters((prev) => ({ ...prev, [key]: value }));
  const setTableLevel = (value) =>
    setDemandSelectedFilter("tableLevel", value);
  const setTimeGranularity = (value) =>
    setCommonSelectedFilter("timeGranularity", value);
  const setBgState = (value) => setCommonSelectedFilter("bgState", value);
  const setSubcategoryState = (value) =>
    setCommonSelectedFilter("subcategoryState", value);
  const setBranchState = (value) =>
    setCommonSelectedFilter("branchState", value);
  const setClusterState = (value) =>
    setDemandSelectedFilter("clusterState", value);
  const setDepotState = (value) => setDemandSelectedFilter("depotState", value);
  const setBrandState = (value) => setCommonSelectedFilter("brandState", value);
  const setFormat1State = (value) =>
    setDemandSelectedFilter("format1State", value);
  const setFormatState = (value) =>
    setCommonSelectedFilter("formatState", value);
  const setBasepackState = (value) =>
    setCommonSelectedFilter("basepackState", value);
  const setYearState = (value) => setCommonSelectedFilter("yearState", value);
  const setQuarterState = (value) =>
    setCommonSelectedFilter("quarterState", value);
  const setMonthState = (value) => setCommonSelectedFilter("monthState", value);  
  const setLoad3Years = (value) =>
    setCommonSelectedFilter("load_ym", value);
  const setAppliedFilters = (value) => {
    setCommonSelectedFilter(
      "appliedFilters",
      value.filter((filter) => commonAppliedFilterNames.includes(filter))
    );
    setDemandSelectedFilter(
      "appliedFilters",
      value.filter((filter) => !commonAppliedFilterNames.includes(filter))
    );
  };
  const [result, setResult] = useState({
    graph: {
      labels: [],
      growth: [],
      bars: [],
    },
    table: {
      header: [],
      data: [],
    },
  });
  const [chartInstance, setChartInstance] = useState(null);
  function changeMonthFormat(month) {
    if (timeGranularity === "MoM") {
      let month_value = month.split("-");
      month_value =
        monthNames[Number.parseInt(month_value[1]) - 1] + "-" + month_value[0];

      return month_value;
    } else {
      return month;
    }
  }
  const currentPoint = (() => {
    const now = new Date();
    const yearQuarter = `${now.getFullYear()}-Q${
      Math.floor(now.getMonth() / 3) + 1
    }`;
    switch (timeGranularity) {
      case "YoY":
        return now.toLocaleString("en-US", { year: "numeric" });
      case "MoM":
        return now
          .toLocaleString("en-US", { month: "short", year: "numeric" })
          .replace(" ", "-");
      case "QoQ":
        return yearQuarter;
      default:
        return null;
    }
  })();
  const onChartReady = (echartInstance) => {
    setChartInstance(echartInstance);
  };
  function parseLabelToDate(label) {
    if (timeGranularity === "YoY") {
      return new Date(parseInt(label), 0, 1);
    } else if (timeGranularity === "QoQ") {
      const [year, quarter] = label.split("-Q");
      const month = (parseInt(quarter) - 1) * 3;
      return new Date(parseInt(year), month, 1);
    } else {
      const [monthStr, yearStr] = label.split("-");
      const monthIndex = monthNames.indexOf(monthStr);
      if (monthIndex === -1) return new Date(NaN);
      return new Date(parseInt(yearStr, 10), monthIndex, 1);
    }
  }

  const getGraphics = () => {
    const xAxisData = result.graph.labels.map((item) =>
      changeMonthFormat(item)
    );
    console.log("xAxisData: ", xAxisData);
    const currentDate = new Date();
    console.log("currentDate: ", currentDate);
    const dateObjects = xAxisData.map((label) => parseLabelToDate(label));
    console.log("dateObjects: ", dateObjects);
    const hasPast = dateObjects.some((date) => date < currentDate);
    const hasFuture = dateObjects.some((date) => date >= currentDate);
    console.log(hasPast, hasFuture);

    const graphic = [];

    let salesIndex = null;
    let demandIndex = null;

    if (hasPast && hasFuture) {
      const currentIndex = dateObjects.findIndex((date) => date >= currentDate);
      salesIndex = Math.floor(currentIndex / 2);
      demandIndex = Math.floor((currentIndex + dateObjects.length) / 2);
    } else if (hasPast) {
      salesIndex = Math.floor(dateObjects.length / 2);
    } else if (hasFuture) {
      demandIndex = Math.floor(dateObjects.length / 2);
    }

    if (salesIndex !== null) {
      const salesX = chartInstance.convertToPixel(
        { xAxisIndex: 0 },
        xAxisData[salesIndex]
      );
      graphic.push(createLabel("Sales", salesX));
    }

    if (demandIndex !== null) {
      const demandX = chartInstance.convertToPixel(
        { xAxisIndex: 0 },
        xAxisData[demandIndex]
      );
      graphic.push(createLabel("Forecast Demand", demandX));
    }

    chartInstance.setOption({ graphic }, false);
  };

  
  const labels = result.graph.labels.map(item => changeMonthFormat(item));

  const splitIndex = labels.findIndex(
    label => label === currentPoint
  );

  const barData = Object.values(result.graph.bars).reduce((acc, curr) => {
    return acc.map((sum, index) => sum + curr?.data[index]);
  }, new Array(Object.values(result.graph.bars)[0]?.data.length).fill(0));


  let option = {
    legend: {
      selectedMode: true,
      top: "bottom",
      icon: "rect",
      itemWidth: 6,
      itemHeight: 15,
    },
    grid: {
      left: 70,
      right: 70,
      top: 30,
      bottom: 50,
    },
    tooltip: {
      trigger: "axis",
    },
    yAxis: [
      {
        axisLabel: {
          formatter: "{value}",
        },
        type: "value",
        splitLine: {
          show: false,
        },
        name: "Demand",
        nameLocation: "middle",
        nameRotate: 90,
        nameGap: 50,
        axisLabel: {
          textStyle: {
            fontSize: 10,
          },
        },
      },
      {
        axisLabel: {
          formatter: "{value}%",
        },
        type: "value",
        splitLine: {
          show: false,
        },
        axisLabel: {
          textStyle: {
            fontSize: 10,
          },
        },
        name: "Growth %",
        nameLocation: "middle",
        nameRotate: -90,
        nameGap: 50,
      },
    ],
    xAxis: {
      type: "category",
      splitLine: {
        show: false,
      },
      data: result.graph.labels.map((item) => changeMonthFormat(item)),
      axisLabel: {
        textStyle: { fontSize: 10 },
      },
    },
    series: [
        {
        name: subcategoryState === "All" ? bgState : subcategoryState,
        type: "bar",
        // stack: "total",
        barWidth: "50%",
        data:  barData.map((value, index) => ({
              value,
              itemStyle: {
                color: index <= splitIndex
                  ? "#00b190" // Historical
                  : "#1F36C7", // Forecast
              },
          })),
        itemStyle: {
          borderRadius: [3, 3, 0, 0],
          color: "#1F36C7",
        },
        tooltip: {
          valueFormatter: function (value) {
            return roundOffNumber(value);
          },
        },
        markLine: {
          symbol: "none",
          label: { show: false },
          data: [
            {
              xAxis: currentPoint,
              lineStyle: {
                type: "dashed",
                width: 1,
                color: "#525252",
              },
            },
          ],
        },
      },
      {
        type: "line",
        name: "YoY Growth%",
        data: result.graph.growth,
        yAxisIndex: 1,
        itemStyle: {
          color: "#FF9E00",
        },
        lineStyle: {
          color: "#FF9E00",
          width: 1,
        },
        smooth: true,
        symbolSize: 7,
        symbol: "circle",
        tooltip: {
          valueFormatter: function (value) {
            return roundOffNumber(value) + "%";
          },
        },
        label: {
          show: true,
          position: "top",
          formatter: function (params) {
            return roundOffNumber(params.value) + "%";
          },
          backgroundColor: "white",
          borderRadius: 4,
          padding: [2, 4],
        },
      },
    ],
    color: ["#1F36C7", "#00b190", "#4C64FF", "#846CE2", "#C46CE2"],
  };

  const exportToExcel = () => {
    const table = tableRef.current;
    const worksheet = XLSX.utils.table_to_sheet(table); // Convert table to sheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const dataBlob = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });

    saveAs(dataBlob, "demand-data.xlsx");
  };

  async function fetchDemandData() {
    setLoading(true);

    const resp = await Promise.all([
      getDemandData(userEmail, {
        demandFile,
        bgState,
        subcategoryState,
        branchState: branchFilterValueConvert(branchState),
        clusterState: clusterFilterValueConvert(clusterState),
        depotState,
        brandState,
        format1State,
        formatState,
        basepackState,
        factoryState,
        yearState,
        quarterState,
        monthState: monthFilterValueConvert(monthState),
        timeGranularity,
        runtime,
        runtype,
        table_level: tableLevel,
        year_3: load_ym,
        
      }),
      getDemandFilters(userEmail, {
        demandFile,
        bgState,
        subcategoryState,
        branchState: branchFilterValueConvert(branchState),
        clusterState: clusterFilterValueConvert(clusterState),
        depotState,
        brandState,
        format1State,
        formatState,
        basepackState,
        runtime,
        runtype,
        time_gran: timeGranularity,
        year_3: load_ym,
      }),
    ]);

    const data = resp[0];
    if (resp[1] === 401) return navigate(ltcpRoutes.HOME);
    const filters = resp[1];

    appliedFilters.forEach((filter) => {
      filters[filter] = currentFilters[filter];
    });

    filters.brand = currentFilters.brand;

    setResult(data);
    setCurrentFilters(filters);
    setLoading(false);
  }

  const hierarchyHeaderLabels =
  HIERARCHY_HEADER_LABELS[tableLevel] ?? [];

const dynamicHierarchyHeader = hierarchyHeaderLabels
  .slice(0, visibleHierarchyLevel + 1)
  .join(" | ");
  
  const handleExpandAll = () => {
    const nextExpandAll = !expandAll;

    setExpandAll(nextExpandAll);

    const hierarchyLength =
      HIERARCHY_HEADER_LABELS[tableLevel]?.length ?? 1;

    if (nextExpandAll) {
      /*
      * Expand All shows the full hierarchy including Basepack.
      */
      setVisibleHierarchyLevel(
        hierarchyLength - 1
      );
    } else {
      /*
      * Collapse All returns to the root level.
      */
      setVisibleHierarchyLevel(0);
    }
  };

  const handleHierarchyColumnToggle = () => { 
    setIsHierarchyColumnExpanded((previousValue) => !previousValue);
  };

  useEffect(() => {
  setExpandAll(false);
  setVisibleHierarchyLevel(0);
  setIsHierarchyColumnExpanded(false);
}, [tableLevel]);
  
  useEffect(() => {
    if (!chartInstance || !result?.graph?.labels?.length) return;

    getGraphics(chartInstance, result);
  }, [
    chartInstance,
    result,
    bgState,
    subcategoryState,
    branchState,
    clusterState,
    depotState,
    brandState,
    format1State,
    formatState,
    basepackState,
    factoryState,
    yearState,
    quarterState,
    monthState,
    timeGranularity,
    expandAll,
    loading
  ]);


  useEffect(() => {
    if (demandFilters.bg.length === 0) return navigate(ltcpRoutes.LANDING);

    fetchDemandData();
  }, [
    bgState,
    subcategoryState,
    branchState,
    clusterState,
    depotState,
    brandState,
    format1State,
    formatState,
    basepackState,
    factoryState,
    yearState,
    quarterState,
    monthState,
    timeGranularity,
    tableLevel,
    load_ym,
  ]);
  

  const nestedRowsResetKey = [
    demandFile,
    bgState,
    subcategoryState,
    branchState,
    clusterState,
    depotState,
    brandState,
    format1State,
    formatState,
    basepackState,
    factoryState,
    yearState,
    quarterState,
    monthState,
    timeGranularity,
    tableLevel,
    runtime,
    runtype,
    load_ym,
  ].join("|");

  const nestedRowFilters = {
    demandFile,
    bgState,
    subcategoryState,
    branchState: branchFilterValueConvert(branchState),
    clusterState: clusterFilterValueConvert(clusterState),
    depotState,
    brandState,
    format1State,
    formatState,
    basepackState,
    factoryState,
    yearState,
    quarterState,
    monthState: monthFilterValueConvert(monthState),
    timeGranularity,
    runtime,
    runtype,
    year_3: load_ym,
    resetKey: nestedRowsResetKey,
  };

  const tooltipSlotProps = {
    tooltip: {
      sx: {
        backgroundColor: "#fff",
        color: "#111",
        border: "1px solid #d0d0d0",
        boxShadow:
          "0 2px 8px rgba(0, 0, 0, 0.18)",
        fontSize: 12,
        fontWeight: 500,
      },
    },

    arrow: {
      sx: {
        color: "#fff",

        "&::before": {
          border: "1px solid #d0d0d0",
        },
      },
    },
  };

  return (
    <Stack p={4} py={2} gap={2}>
      <Stack direction="row" gap={2} alignItems="center">
        <OverviewButton
            label="Overview"
            colorFrom="#2563eb"
            colorTo="#1f36c7"
            onClick={() => navigate(ltcpRoutes.DASHBOARD)}
          />
        <OverviewButton
          label="CU%"
          colorFrom="#EC4887"
          colorTo="#B954A4"
          onClick={() => navigate(ltcpRoutes.CU)}
        />
        <OverviewButton
          label="Capacity"
          colorFrom="#46C5F2"
          colorTo="#6692D9"
          onClick={() => navigate(ltcpRoutes.CAPACITY)}
        />
      </Stack>
      <Stack height={40} direction="row" alignItems="center" gap={2}>
        {/* <IconButton
          sx={{ color: "black" }}
          onClick={() => navigate(ltcpRoutes.DASHBOARD)}
        >
          <KeyboardBackspaceIcon />
        </IconButton> */}
        <Typography fontSize={21} fontWeight="bold">
          Demand Summary
        </Typography>
      </Stack>
      <Paper sx={{ height: 60 }}>
        <Stack
          py={1}
          direction="row"
          alignItems="center"
          justifyContent="space-evenly"
        >
          <Stack width="7.8%">
            <Typography fontSize={12}>BG</Typography>
            <MultiSearchFilter
              value={bgState}
              setValue={setBgState}
              options={currentFilters.bg}
              filterName="bg"
              appliedFilters={appliedFilters}
              setAppliedFilters={setAppliedFilters}
            />
          </Stack>
          <Stack width="7.8%">
            <Typography fontSize={12}>Sub-Category</Typography>
            <MultiSearchFilter
              removeUnderscore
              value={subcategoryState}
              setValue={setSubcategoryState}
              options={currentFilters.subcategory}
              filterName="subcategory"
              appliedFilters={appliedFilters}
              setAppliedFilters={setAppliedFilters}
            />
          </Stack>
          <Stack width="7.8%">
            <Typography fontSize={12}>Branch</Typography>
            <MultiSearchFilter
              value={branchState}
              setValue={setBranchState}
              options={currentFilters.branch
                .map((item) => BRANCH_STATE_MAPPING[item] || "Null")
                .sort((a, b) => a.localeCompare(b))}
              filterName="branch"
              appliedFilters={appliedFilters}
              setAppliedFilters={setAppliedFilters}
            />
          </Stack>
          <Stack width="7.8%">
            <Typography fontSize={12}>Cluster</Typography>
            <MultiSearchFilter
              value={clusterState}
              setValue={setClusterState}
              options={currentFilters.cluster
                .map((item) => CLUSTER_STATE_MAPPING[item] || "Null")
                .sort((a, b) => a.localeCompare(b))}
              filterName="cluster"
              appliedFilters={appliedFilters}
              setAppliedFilters={setAppliedFilters}
            />
          </Stack>
          <Stack width="7.8%">
            <Typography fontSize={12}>Depot</Typography>
            <MultiSearchFilter
              value={depotState}
              setValue={setDepotState}
              options={currentFilters.depot}
              filterName="depot"
              appliedFilters={appliedFilters}
              setAppliedFilters={setAppliedFilters}
            />
          </Stack>
          <Stack width="7.8%">
            <Typography fontSize={12}> Brand
              {/* Brand <span style={{ color: "red" }}> *</span> */}
            </Typography>
            <MultiSearchFilter
              value={brandState}
              setValue={setBrandState}
              options={currentFilters.brand}
              filterName="brand"
              appliedFilters={appliedFilters}
              setAppliedFilters={setAppliedFilters}
            />
            {/* <SearchFilter
              value={brandState}
              setValue={setBrandState}
              options={currentFilters.brand}
            /> */}
          </Stack>
          <Stack width="7.8%">
            <Typography fontSize={12}>Format L1</Typography>
            <MultiSearchFilter
              value={format1State}
              setValue={setFormat1State}
              options={currentFilters.format1}
              filterName="format1"
              appliedFilters={appliedFilters}
              setAppliedFilters={setAppliedFilters}
            />
          </Stack>
          <Stack width="7.8%">
            <Typography fontSize={12}>Format L2</Typography>
            <MultiSearchFilter
              value={formatState}
              setValue={setFormatState}
              options={currentFilters.format}
              filterName="format"
              appliedFilters={appliedFilters}
              setAppliedFilters={setAppliedFilters}
            />
          </Stack>
          <Stack width="7.8%">
            <Typography fontSize={12}>Basepack</Typography>
            <MultiSearchFilter
              value={basepackState}
              setValue={setBasepackState}
              options={currentFilters.basepack}
              filterName="basepack"
              appliedFilters={appliedFilters}
              setAppliedFilters={setAppliedFilters}
            />
          </Stack>
          <Stack width="7.8%">
            <Typography fontSize={12}>Year</Typography>
            <MultiSearchFilter
              options={currentFilters.year}
              value={yearState}
              setValue={(v) => {
                setLoad3Years(false);
                setYearState(v);
              }}
              filterName="year"
              appliedFilters={appliedFilters}
              setAppliedFilters={setAppliedFilters}
            />
          </Stack>
          <Stack width="7.8%">
            <Typography fontSize={12}>Quarter</Typography>
            <MultiSearchFilter
              options={["Q1", "Q2", "Q3", "Q4"]}
              value={quarterState}
              setValue={(v) => {
                setLoad3Years(false);
                setQuarterState(v);
              }}
              disabled={timeGranularity === "MoM" || timeGranularity === "YoY"}
              filterName="quarter"
              appliedFilters={appliedFilters}
              setAppliedFilters={setAppliedFilters}
            />
          </Stack>
          <Stack width="7.8%">
            <Typography fontSize={12}>Month</Typography>
            <MultiSearchFilter
              value={monthState}
              setValue={(v) => {
                setLoad3Years(false);
                setMonthState(v);
              }}
              options={[
                "Jan",
                "Feb",
                "Mar",
                "Apr",
                "May",
                "Jun",
                "Jul",
                "Aug",
                "Sep",
                "Oct",
                "Nov",
                "Dec",
              ]}
              disabled={timeGranularity === "QoQ" || timeGranularity === "YoY"}
              filterName="month"
              appliedFilters={appliedFilters}
              setAppliedFilters={setAppliedFilters}
            />
          </Stack>
        </Stack>
      </Paper>
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "calc(50vh - 128px)",
          minHeight: "calc(50vh - 128px)",
          maxHeight: "calc(50vh - 128px)",
        }}
      >
        <Stack
          p={1}
          gap={1}
          direction="row"
          alignItems="center"
          justifyContent="end"
        >
          <ToggleButtonGroup
            value={timeGranularity}
            onChange={(_, v) => {
              if (v === null) return;

              setTimeGranularity(v);
              if (v === "YoY") {
                setQuarterState("All");
                setMonthState("All");
              } else if (v === "QoQ") {
                setMonthState("All");
              } else if (v === "MoM") {
                setQuarterState("All");
              }
            }}
            exclusive
          >
            <ToggleButton value="YoY">YoY</ToggleButton>
            <ToggleButton value="QoQ">QoQ</ToggleButton>
            <ToggleButton value="MoM">MoM</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        <Box sx={{ flexGrow: 1, display: "flex" }}>
          <ReactECharts
            notMerge={true}
            option={option}
            style={{ width: "100%", height: "100%" }}
            onChartReady={onChartReady}
          />
        </Box>
      </Paper>
      
      <Paper
        sx={{
          p: 1,
          display: "flex",
          flexDirection: "column",
          height: "calc(50vh - 128px)",
          minHeight: "calc(50vh - 128px)",
          maxHeight: "calc(50vh - 128px)",
        }}
      >
        <Stack
          p={1}
          gap={2}
          direction="row"
          justifyContent="end"
          alignItems="center"
        >
          <ToggleButtonGroup
            exclusive
            value={tableLevel}
            onChange={(_, v) => v !== null && setTableLevel(v)}
          >
            <ToggleButton value="national">National</ToggleButton>
            <ToggleButton value="branch">Branch</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        <Box sx={{ display: "flex", height: "calc(100% - 35px)" }}>
          <TableContainer
              sx={{
                width: "100%",
                overflowX: "auto",
                overflowY: "auto",
              }}
            >
              <Table
                ref={tableRef}
                stickyHeader
                size="small"
                sx={{
                  width: "max-content",
                  minWidth: "100%",
                  tableLayout: "fixed",
                }}
              >
                {/*
                  colgroup controls the width of the entire column.

                  Changing the first col width updates:
                  - First header cell
                  - Root rows
                  - Nested rows
                  - Basepack leaf rows
                */}
                <colgroup>
                  <col
                    style={{
                      width: `${hierarchyColumnWidth}px`,
                      minWidth: `${hierarchyColumnWidth}px`,
                      maxWidth: `${hierarchyColumnWidth}px`,
                    }}
                  />

                  {result.table.header.map((column) => (
                    <col
                      key={`data-column-${column}`}
                      style={{
                        width: `${DATA_COLUMN_WIDTH}px`,
                        minWidth: `${DATA_COLUMN_WIDTH}px`,
                      }}
                    />
                  ))}

                  {timeGranularity === "YoY" && (
                    <col
                      style={{
                        width: `${DATA_COLUMN_WIDTH}px`,
                        minWidth: `${DATA_COLUMN_WIDTH}px`,
                      }}
                    />
                  )}
                </colgroup>

                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        width: hierarchyColumnWidth,
                        minWidth: hierarchyColumnWidth,
                        maxWidth: hierarchyColumnWidth,
                        background: "#354acd",
                        p: 0,
                        top: 0,
                        position: "sticky",
                        overflow: "hidden",
                        transition:
                          "width 200ms ease, min-width 200ms ease, max-width 200ms ease",
                      }}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          height: "100%",
                          px: 0.5,
                          display: "flex",
                          alignItems: "center",
                          overflow: "hidden",
                        }}
                      >
                        <Tooltip title="Download" placement="top">
                          <IconButton
                            sx={{
                              color: "#fff",
                              flexShrink: 0,
                            }}
                            size="small"
                            onClick={exportToExcel}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip
                          title={expandAll ? "Collapse All" : "Expand All"}
                          placement="top"
                        >
                          <IconButton
                            sx={{
                              color: "#fff",
                              flexShrink: 0,
                            }}
                            size="small"
                            onClick={handleExpandAll}
                          >
                            {expandAll ? (
                              <CloseFullscreenIcon fontSize="small" />
                            ) : (
                              <OpenInFullIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>

                    <TableCell
                      colSpan={result.table.header.length / 2}
                      sx={{
                        top: 0,
                        background: "#354acd",
                        color: "#fff",
                        borderLeft: "1px solid #fff",
                        textAlign: "center",
                        textWrap: "nowrap",
                        position: "sticky",
                      }}
                    >
                      Historical Sales and Forecasted Demand(T)
                    </TableCell>

                    <TableCell
                      colSpan={result.table.header.length / 2}
                      sx={{
                        top: 0,
                        background: "#354acd",
                        color: "#fff",
                        borderLeft: "1px solid #fff",
                        textAlign: "center",
                        textWrap: "nowrap",
                        position: "sticky",
                      }}
                    >
                      {timeGranularity} Growth
                    </TableCell>

                    {timeGranularity === "YoY" && (
                      <TableCell
                        sx={{
                          top: 0,
                          background: "#354acd",
                          color: "#fff",
                          borderLeft: "1px solid #fff",
                          textAlign: "center",
                          textWrap: "nowrap",
                          position: "sticky",
                        }}
                      >
                        CAGR
                      </TableCell>
                    )}
                  </TableRow>

                  <TableRow>
                    <TableCell
                      sx={{
                        top: 34,
                        p: 0,
                        width: hierarchyColumnWidth,
                        minWidth: hierarchyColumnWidth,
                        maxWidth: hierarchyColumnWidth,
                        height: 40,
                        background: "#354acd",
                        color: "#fff",
                        position: "sticky",
                        overflow: "hidden",
                        transition:
                          "width 200ms ease, min-width 200ms ease, max-width 200ms ease",
                      }}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          height: "100%",
                          minWidth: 0,
                          display: "flex",
                          alignItems: "center",
                          overflow: "hidden",
                        }}
                      >
                        <Tooltip
                            title={dynamicHierarchyHeader}
                            placement="top"
                            arrow
                            slotProps={tooltipSlotProps}
                          >
                          <Typography
                            component="span"
                            sx={{
                              pl: 1.5,
                              pr: 0.5,
                              flex: 1,
                              minWidth: 0,
                              color: "#fff",
                              fontSize: 12,
                              fontWeight: 600,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {dynamicHierarchyHeader}
                          </Typography>
                        </Tooltip>

                        <Tooltip
                          title={
                            isHierarchyColumnExpanded
                              ? "Collapse hierarchy column"
                              : "Expand hierarchy column"
                          }
                          placement="top"
                          arrow
                          slotProps={tooltipSlotProps}
                        >
                          <IconButton
                            size="small"
                            onClick={handleHierarchyColumnToggle}
                            aria-label={
                              isHierarchyColumnExpanded
                                ? "Collapse hierarchy column"
                                : "Expand hierarchy column"
                            }
                            sx={{
                              mr: 0.5,
                              flexShrink: 0,
                              color: "#fff",
                              backgroundColor: "rgba(255, 255, 255, 0.12)",

                              "&:hover": {
                                backgroundColor: "rgba(255, 255, 255, 0.22)",
                              },
                            }}
                          >
                            {isHierarchyColumnExpanded ? (
                              <ChevronLeftIcon fontSize="small" />
                            ) : (
                              <ChevronRightIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>

                    {result.table.header.map((item, index) => (
                      <TableCell
                        key={item || index}
                        sx={{
                          top: 34,
                          width: DATA_COLUMN_WIDTH,
                          minWidth: DATA_COLUMN_WIDTH,
                          background: "#354acd",
                          color: "#fff",
                          borderLeft: "1px solid #fff",
                          textWrap: "nowrap",
                          position: "sticky",
                          textAlign: "right",
                        }}
                      >
                        {changeMonthFormat(item.split("_")[1])}
                      </TableCell>
                    ))}

                    {timeGranularity === "YoY" && (
                      <TableCell
                        sx={{
                          top: 34,
                          width: DATA_COLUMN_WIDTH,
                          minWidth: DATA_COLUMN_WIDTH,
                          background: "#354acd",
                          color: "#fff",
                          borderLeft: "1px solid #fff",
                          textWrap: "nowrap",
                          position: "sticky",
                          textAlign: "right",
                        }}
                      >
                        CAGR 2Y
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {result.table.data.map((item, index) => {
                    const rootIdentifier =
                      tableLevel === "branch"
                        ? item.branch
                        : item.subcategory ?? item.sub_category;

                    return (
                      <DemandNestedRow
                        key={
                          `${tableLevel}-${rootIdentifier}-${index}-` +
                          nestedRowsResetKey
                        }
                        data={item}
                        header={result.table.header}
                        tableLevel={tableLevel}
                        level={0}
                        filters={nestedRowFilters}
                        expandAll={expandAll}
                        ancestry={[]}
                        onVisibleLevelChange={setVisibleHierarchyLevel}
                        hierarchyColumnWidth={hierarchyColumnWidth}
                        isHierarchyColumnExpanded={isHierarchyColumnExpanded}
                      />
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
        </Box>
        <Stack
          mt={2}
          gap={1}
          height={30}
          direction="row"
          alignItems="center"
          justifyContent="end"
        >
          <Box sx={{ width: 12, height: 12, background: "#ff9998" }}></Box>
          <Typography mr={1} fontSize={12}>
            {"< 0"}
          </Typography>
          <Box sx={{ width: 12, height: 12, background: "#ffec9a" }}></Box>
          <Typography mr={1} fontSize={12}>
            {"0 - 10"}
          </Typography>
          <Box sx={{ width: 12, height: 12, background: "#a3e1d2" }}></Box>
          <Typography mr={1} fontSize={12}>
            {"> 10"}
          </Typography>
        </Stack>
      </Paper>
    </Stack>
  );
}

export default DemandPage;
